import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { areaService, Area } from '../services/areaService';

interface AreaContextType {
    currentArea: Area | null;
    availableAreas: Area[];
    loading: boolean;
    error: string | null;
    setCurrentArea: (area: Area) => Promise<void>;
    clearCurrentArea: () => void;
    fetchAvailableAreas: (entityId: string) => Promise<void>;
}

const AreaContext = createContext<AreaContextType | undefined>(undefined);

export const useArea = () => {
    const context = useContext(AreaContext);
    if (!context) {
        throw new Error('useArea must be used within an AreaProvider');
    }
    return context;
};

interface AreaProviderProps {
    children: ReactNode;
}

export const AreaProvider: React.FC<AreaProviderProps> = ({ children }) => {
    const [currentArea, setCurrentAreaState] = useState<Area | null>(null);
    const [availableAreas, setAvailableAreas] = useState<Area[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAvailableAreas = useCallback(async (entityId: string) => {
        try {
            setLoading(true);
            setError(null);

            const areas = await areaService.getAvailableAreas(entityId);
            setAvailableAreas(areas);
        } catch (err) {
            setError('Erro ao carregar áreas disponíveis');
            console.error('Error fetching areas:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const setCurrentArea = useCallback(async (area: Area) => {
        try {
            setCurrentAreaState(area);
            localStorage.setItem('currentAreaId', area.area_id);
        } catch (err) {
            setError('Erro ao definir a área atual');
            console.error('Error setting current area:', err);
        }
    }, []);

    const clearCurrentArea = useCallback(() => {
        localStorage.removeItem('currentAreaId');
        setCurrentAreaState(null);
    }, []);

    const value = useMemo(() => ({
        currentArea,
        availableAreas,
        loading,
        error,
        setCurrentArea,
        clearCurrentArea,
        fetchAvailableAreas
    }), [
        currentArea,
        availableAreas,
        loading,
        error,
        setCurrentArea,
        clearCurrentArea,
        fetchAvailableAreas
    ]);

    return (
        <AreaContext.Provider value={value}>
            {children}
        </AreaContext.Provider>
    );
}; 